import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { hrefOf, linkKey, type AiLink } from '@d-orca/shared/navigation';
import { ArrowRightIcon } from '@utils/icons';
import { DESTINATION_META } from './destinationMeta';
import styles from './styles.module.css';

interface IAiAnswerLinksProps {
  /** The conversation's site. A link carries none of its own, so it can open no other. */
  siteId: string;
  links: readonly AiLink[];
}

/** What a chip says after the page's name: its filters in words, then the record it opens. */
function facetsOf(link: AiLink, t: TFunction): string[] {
  const facets: string[] = [];
  for (const [param, key] of Object.entries(DESTINATION_META[link.destination].facets ?? {})) {
    const value = link.params[param];
    if (value === true) facets.push(t(key));
    else if (Array.isArray(value)) facets.push(value.map((member) => t(`${key}.${String(member)}`)).join(', '));
    else if (typeof value === 'string') facets.push(t(`${key}.${value}`));
  }
  if (link.caption !== undefined) facets.push(link.caption);
  return facets;
}

/**
 * The places an answer offers to take the reader, as a row of chips under it.
 *
 * REAL ANCHORS, so opening one in a new tab, copying it and reaching it by
 * keyboard all work as they do for any link. The label is never the model's
 * words: it is the page's own name and the link's filters read through i18n,
 * plus the caption the tool took from the record — so a chip cannot promise
 * anything the page it opens does not show.
 */
export function AiAnswerLinks({ siteId, links }: IAiAnswerLinksProps) {
  const { t } = useTranslation();
  if (links.length === 0) return null;

  return (
    <nav className={styles.links} aria-label={t('aiAgent.link.region')}>
      {links.map((link, n) => {
        const meta = DESTINATION_META[link.destination];
        const facets = facetsOf(link, t);
        return (
          <Link key={`${n}-${linkKey(link)}`} to={hrefOf(siteId, link)} className={styles.link}>
            <span className={styles.icon}>{meta.icon}</span>
            <span className={styles.title}>{t(meta.titleKey)}</span>
            {facets.length > 0 && <span className={styles.facets}>{facets.join(' · ')}</span>}
            <span className={styles.arrow}>
              <ArrowRightIcon size={12} />
            </span>
          </Link>
        );
      })}
    </nav>
  );
}
